import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigService,
    CONNECTION_ERROR_CODES,
    EncodingUtilities,
    ENDPOINTS,
    EnvironmentPathUtilitiesService,
    NotificationInterface,
    OnlineOnlyService,
    SYNC_REQUEST_TYPE,
    SynchronizationInstanceToken,
    SynchronizationInterface,
    SyncQueueService,
    UriUtilitiesService,
    UserMasterService,
    WindowService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatAll, filter, map, mergeMap, tap } from 'rxjs/operators';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';
import { FormState } from '../store/state/form.state';
import { FormIdentifier } from '../types/form-identifier';
import { FormsConstants } from '../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../types/functional-element';
import { ParticipantCopyId } from '../types/participant-copy';
import { Signature } from '../types/signature';
import { FormUtilities } from '../utilities/form-utilities';
import { NotificationUtilities } from '../utilities/notification-utilities';
import {
    FormsWriteInterface,
    ODataRefreshXMLPayload,
    ODataReleaseLockPayload,
    ODataSubmitFormPayload,
    ODataSubmitParticipantCopyPayload,
} from './forms-write.interface';
import { XmlParserService } from './xml-parser.service';
import { XmlSerializerService } from './xml-serializer.service';
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;
import FORMTYPE = FormsConstants.FORMTYPE;

function isRequestError(error: unknown): error is ODataRequestError {
    const odataError = <ODataRequestError>error;
    return !!odataError.response && !!odataError.response.body;
}

@Injectable({
    providedIn: 'root',
})
export class FormsWriteWithPaceOfflineService implements FormsWriteInterface {
    private window: Window;
    public constructor(
        private readonly http: HttpClient,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        window: WindowService,
        private readonly configService: ConfigService,
        @Inject(SynchronizationInstanceToken) private readonly synchronizationService: SynchronizationInterface,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        private readonly userMasterService: UserMasterService,
        private readonly syncQueueService: SyncQueueService,
        private readonly xmlParserService: XmlParserService,
        private readonly xmlSerializerService: XmlSerializerService,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly environmentPathUtilities: EnvironmentPathUtilitiesService,
        protected readonly onlineOnlyService: OnlineOnlyService,
    ) {
        this.window = window.nativeWindow;
    }

    public submitMaster(
        form: FormState,
        saveMode: FormsConstants.SUBMIT_STATUS,
    ): Observable<void | CONNECTION_ERROR_CODES> {
        const notification = this.notificationService.getNotificationStateByIdentifier(form.notification);

        if (typeof notification === 'undefined') {
            throw new Error(
                `FormsWriteMockService: submitForm: Could not load notification '${JSON.stringify(form.notification)}'`,
            );
        }

        const equipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (typeof equipment === 'undefined') {
            throw new Error(
                `FormsWriteMockService: submitForm: Notification '${JSON.stringify(
                    form.notification,
                )}' has no main equipment!`,
            );
        }

        this.log.debug(
            `FormsWriteMockService: submitMaster: Submitting form '${
                form.formId.formId
            }' for notification '${NotificationUtilities.toString(form.notification)}'`,
        );

        const user = this.userMasterService.getSelectedUser();

        const formQuery = this.getBasePath() + ENDPOINTS.F4mSubmitFormSet;

        // clean the XML and retrieve it
        this.xmlParserService.setXml(form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].xml);
        this.xmlSerializerService.cleanMasterXml(saveMode, form.type);
        const cleanedXml = this.xmlParserService.getXml(form.doctype);

        // restore XML of currently viewed participant
        this.xmlParserService.setXml(form.participantCopies[form.selectedParticipantCopyId].xml);

        // encode the XML for transfer with base64
        const encodedXml = EncodingUtilities.base64EncodeUnicode(cleanedXml);

        const employeeSignatureFunctionalElementType = FormUtilities.isAstoriaForm(form.type)
            ? FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE
            : FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE;
        const employeeSignature =
            form.supportParticipantCopies ||
            form.type === FormsConstants.FORMTYPE.TRAININGCHECKLIST ||
            (FormUtilities.isCosimaForm(form.type) && !form.systemCondition?.employeeSignature)
                ? (Object.values(form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].functionalElements).find(
                      (fe) => fe.type === employeeSignatureFunctionalElementType,
                  ) as Signature | undefined)
                : form.systemCondition?.employeeSignature;
        if (typeof employeeSignature === 'undefined') {
            this.log.warn(`FormsWriteService: submitMaster: No employee signature found for form '${form.formId}'`);
        }

        const customerSignatureFunctionalElementType = FormUtilities.isAstoriaForm(form.type)
            ? FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER
            : FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE;
        const customerSignature =
            form.supportParticipantCopies ||
            form.type === FormsConstants.FORMTYPE.TRAININGCHECKLIST ||
            (FormUtilities.isCosimaForm(form.type) && !form.systemCondition?.customerSignature)
                ? (Object.values(form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].functionalElements).find(
                      (fe) => fe.type === customerSignatureFunctionalElementType,
                  ) as Signature | undefined)
                : form.systemCondition?.customerSignature;
        if (typeof customerSignature === 'undefined') {
            this.log.warn(`FormsWriteService: submitMaster: No customer signature found for form '${form.formId}'`);
        }

        const selectedRecipients = form.recipients.filter((r) => r.selected);

        const employeeSignatureString = employeeSignature?.signature ?? '';
        const customerSignatureString = customerSignature?.signature ?? '';

        const payload: ODataSubmitFormPayload = {
            EquipmentId: form.formId.equipmentId,
            FormDocid: form.formId.formId,
            GID: user.frontId,
            // GW does not know HANDOVER, only INTERIM and FINAL
            Savemode: saveMode === SUBMIT_STATUS.HANDOVER ? SUBMIT_STATUS.INTERIM : saveMode,
            XMLContent: encodedXml,
            Country: user.country,
            CompanyCode: user.salesOrg,
            NotificationNo: form.notification.qmNum,
            TaskNumber: form.notification.maNum,
            NotificationType: notification.notificationType,
            TechIdentNum: equipment.tidnr,
            SerialNum: equipment.sernr,
            CSESignatureFileName: employeeSignature?.entityPath ?? '',
            CSEMimeType: FormUtilities.isAstoriaForm(form.type)
                ? FormsConstants.MIME_TYPE.EPS
                : FormsConstants.MIME_TYPE.PNG,
            CSEBase64: employeeSignatureString.length > 0 ? employeeSignatureString : FormsConstants.EMPTY_SIGNATURE,
            CustomerSignatureFileName: customerSignature?.entityPath ?? '',
            CustomerMimeType: FormUtilities.isAstoriaForm(form.type)
                ? FormsConstants.MIME_TYPE.EPS
                : FormsConstants.MIME_TYPE.PNG,
            CustomerBase64:
                customerSignatureString.length > 0 ? customerSignatureString : FormsConstants.EMPTY_SIGNATURE,
            EquiShortDesc: equipment.equiShortDesc,

            Email_01:
                (form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies) ||
                FormUtilities.isServicePartQuestionnaire(form.type)
                    ? this.configService.getCseEmailFax()
                    : selectedRecipients[0].email,
            Email_02:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? notification.address.fax1
                    : (selectedRecipients[1]?.email ?? ''),
            Email_03:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? notification.address.fax2
                    : (selectedRecipients[2]?.email ?? ''),
            Email_04:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? notification.address.fax3
                    : (selectedRecipients[3]?.email ?? ''),
            Email_05:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? notification.address.fax4
                    : (selectedRecipients[4]?.email ?? ''),
            Email_06:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].email
                    : (selectedRecipients[5]?.email ?? ''),
            Email_07:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? ''
                    : (selectedRecipients[6]?.email ?? ''),
            Email_08:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? ''
                    : (selectedRecipients[7]?.email ?? ''),
            Email_09:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? ''
                    : (selectedRecipients[8]?.email ?? ''),
            Email_10:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? ''
                    : (selectedRecipients[9]?.email ?? ''),
            Material: FormUtilities.isServicePartQuestionnaire(form.type) ? equipment.matnr : undefined,
            ComponentMaterialNumber:
                form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].defectServicePartIdentification?.materialNo
                    .value,
            ComponentSerialNumber:
                form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].defectServicePartIdentification?.serialNo
                    .value,
        };

        // For AA and SA we need to pass Sales Org and Plant in the payload
        if (this.clientManager.isNgmAP0()) {
            payload.SalesOrg = user.salesOrg;
            payload.Plant = user.plant;
        }

        if (this.configService.getEnableManualIndividualization()) {
            payload.ManIndividualizationComponents = form.individualization ? form.manIndividualizationComponents : [];
        }

        return this.postRequest(formQuery, payload).pipe(
            map(() => {}),
            catchError((error) => {
                if (isRequestError(error)) {
                    this.log.error(
                        `FormsWriteMockService: submitForm: Error saving form on gateway ${error.response.body}`,
                    );
                } else {
                    this.log.error(`FormsWriteMockService: submitForm: Error saving form on gateway`);
                }

                // re-throw error
                return throwError(error);
            }),
        );
    }

    public submitParticipantCopies(form: FormState, participantCopyIds: ParticipantCopyId[]): Observable<void> {
        const notification = this.notificationService.getNotificationStateByIdentifier(form.notification);
        if (typeof notification === 'undefined') {
            throw new Error(
                `FormsWriteMockService: submitParticipantCopies: Could not load notification '${JSON.stringify(
                    form.notification,
                )}'`,
            );
        }

        const equipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (typeof equipment === 'undefined') {
            throw new Error(
                `FormsWriteMockService: submitParticipantCopies: Notification '${JSON.stringify(
                    form.notification,
                )}' has no main equipment!`,
            );
        }

        this.log.debug(
            `FormsWriteMockService: submitParticipantCopies: Submitting form '${
                form.formId.formId
            }' for notification '${NotificationUtilities.toString(
                form.notification,
            )}' for ids ${participantCopyIds.join(' ')}`,
        );

        const user = this.userMasterService.getSelectedUser();

        const formQuery = this.getBasePath() + ENDPOINTS.F4mSubmitParticipantCopySet;

        const submit$ = participantCopyIds.map((participantCopyId) => {
            const participantCopy = form.participantCopies[participantCopyId];

            // clean the XML and retrieve it
            this.xmlParserService.setXml(form.participantCopies[participantCopyId].xml);
            this.xmlSerializerService.cleanParticipantXml(
                form.checkpoints,
                form.type,
                form.participantCopies[participantCopyId].options,
                form.participantCopies[participantCopyId].functionalElements,
            );
            const cleanedXml = this.xmlParserService.getXml(form.doctype);

            // encode the XML for transfer with base64
            const encodedXml = EncodingUtilities.base64EncodeUnicode(cleanedXml);

            const employeeSignature = Object.values(participantCopy.functionalElements).find(
                (fe) => fe.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE,
            ) as Signature | undefined;
            if (typeof employeeSignature === 'undefined') {
                this.log.warn(`FormsWriteMockService: submitParticipantCopies: No employee signature found!`);
            }
            const customerSignature = Object.values(participantCopy.functionalElements).find(
                (fe) => fe.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER,
            ) as Signature | undefined;

            if (typeof customerSignature === 'undefined') {
                this.log.warn(`FormsWriteMockService: submitParticipantCopies: No customer signature found!`);
            }

            const payload: ODataSubmitParticipantCopyPayload = {
                EquipmentId: form.formId.equipmentId,
                FormDocid: form.formId.formId,
                GID: user.frontId,
                XMLContent: encodedXml,
                Country: user.country,
                CompanyCode: user.salesOrg,
                NotificationNo: form.notification.qmNum,
                TaskNumber: form.notification.maNum,
                NotificationType: notification.notificationType,
                TechIdentNum: equipment.tidnr,
                SerialNum: equipment.sernr,
                CSESignatureFileName: employeeSignature?.entityPath ?? '',
                CSEMimeType: FormUtilities.isAstoriaForm(form.type)
                    ? FormsConstants.MIME_TYPE.EPS
                    : FormsConstants.MIME_TYPE.PNG,
                CSEBase64: employeeSignature?.signature ?? FormsConstants.EMPTY_SIGNATURE,
                CustomerSignatureFileName: customerSignature?.entityPath ?? '',
                CustomerMimeType: FormUtilities.isAstoriaForm(form.type)
                    ? FormsConstants.MIME_TYPE.EPS
                    : FormsConstants.MIME_TYPE.PNG,
                CustomerBase64: customerSignature?.signature ?? FormsConstants.EMPTY_SIGNATURE,
                ParticipantEmail: participantCopy.email,
                ParticipantName: `${participantCopy.firstName} ${participantCopy.lastName}`,
                EquiShortDesc: equipment.equiShortDesc,
                UniqueID: participantCopy.id,
            };

            return this.postRequest(formQuery, payload);
        });

        // restore XML of currently viewed participant
        this.xmlParserService.setXml(form.participantCopies[form.selectedParticipantCopyId].xml);

        // concatAll serializes an array of observables and allows us to serialize the write access to the offline
        // store. But it emits an event each time an inner observable emits. This would trigger the entire pipeline
        // and therefore dispatch the success action in the calling effect multiple times, one time for each participant
        // copy that was successfully submitted.
        // Since we are only interested in the overall success of the submits, we pass a custom string in as element
        // to indicate when all submit observables are complete. We then filter for that string to block the individual
        // events from triggering the rest of the pipe and let only the final completion event trigger the rest of the
        // pipe.
        return of(...submit$, of('submit of participant copies done')).pipe(
            concatAll(),
            filter((result) => result === 'submit of participant copies done'),
            tap(() =>
                this.log.debug(
                    `FormsWriteMockService: submitParticipantCopies: submitted participant copies for form ${form.formId.formId}`,
                ),
            ),
            map(() => {}),
            catchError((error) => {
                const requestError = <ODataRequestError | undefined>error;

                if (requestError !== undefined) {
                    this.log.error(
                        `FormsWriteMockService: submitParticipantCopies: Error saving form on gateway ${requestError.response.body}`,
                    );
                } else {
                    this.log.error(`FormsWriteMockService: submitParticipantCopies: Error saving form on gateway`);
                }

                // re-throw error
                return throwError(requestError);
            }),
        );
    }

    /**
     * Send the "release lock" command to make the form lockable and editable for somebody else again.
     * Flush the offline store to actually send the data to the backend.
     *
     * @param id The form identifier of the form to release the lock for.
     * @returns An observable that emits when the lock has been released.
     */
    public releaseLock(id: FormIdentifier): Observable<void | CONNECTION_ERROR_CODES> {
        const query = this.getBasePath() + ENDPOINTS.F4mReleaseLock;

        const payload: ODataReleaseLockPayload = {
            EquipmentId: id.equipmentId,
            FormDocid: id.formId,
        };

        return this.postRequest(query, payload).pipe(
            mergeMap(() => {
                // flush the offline store if possible to send data to the backend
                this.log.debug('FormsWriteMockService: releaseLock: sendOfflineData');
                return this.synchronizationService.sendOfflineData();
            }),
            catchError((error) => {
                this.log.error('FormsWriteMockService: releaseLock: no internet connection, add sync request');
                this.syncQueueService.addSyncRequest(SYNC_REQUEST_TYPE.FLUSH_ONLY);
                return throwError(`FormsWriteMockService: releaseLock: error: ${JSON.stringify(error)}`);
            }),
        );
    }

    public refreshXML(form: FormIdentifier): Observable<unknown> {
        const query = ENDPOINTS.RefreshXML;
        const payload: ODataRefreshXMLPayload = {
            EquipmentId: form.equipmentId,
            FormDocid: form.formId,
        };

        return this.postRequest(query, payload).pipe(
            catchError((error) => {
                return throwError(
                    () =>
                        `FormsWriteService: refreshXML: error: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
            }),
        );
    }

    protected postRequest(url: string, payload: ODataSubmitFormPayload | ODataReleaseLockPayload): Observable<unknown> {
        const mockDataPath = this.environmentPathUtilities.getMockDataPath();

        let mockDataUrl;
        if (url.includes(ENDPOINTS.F4mSubmitFormSet)) {
            mockDataUrl = mockDataPath + ENDPOINTS.F4mSubmitFormSet + '.json';
        } else if (url.includes(ENDPOINTS.F4mSubmitParticipantCopySet)) {
            mockDataUrl = mockDataPath + ENDPOINTS.F4mSubmitParticipantCopySet + '.json';
        } else if (url.includes(ENDPOINTS.F4mReleaseLock)) {
            mockDataUrl = mockDataPath + ENDPOINTS.F4mReleaseLock + '.json';
        } else if (url.includes(ENDPOINTS.RefreshXML)) {
            mockDataUrl = mockDataPath + ENDPOINTS.RefreshXML + '.json';
        }

        if (!mockDataUrl) {
            return throwError(
                () => `FormsWriteMockService: postRequest: Could not find suitable mockdata json for url '${url}'`,
            );
        }

        return this.http.post(mockDataUrl, payload);
    }

    public getBasePath(): string {
        return '';
    }
}
